import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { SEO, getHowToSchema } from "../components/SEO";
import { apiFetch } from "../lib/api";

interface Tutorial {
  id: string;
  title: string;
  slug: string;
  description: string;
  content: string;
  coverImage: string | null;
  category: string;
  difficulty: string;
  estimatedTime: number | null;
  videoUrl: string | null;
  viewCount: number;
  createdAt: string;
}

const categoryLabels: Record<string, string> = {
  iniciando: "Iniciando",
  modulos: "Módulos",
  integracoes: "Integrações",
  relatorios: "Relatórios",
  admin: "Administração",
  avancado: "Avançado",
};

const difficultyLabels: Record<string, string> = {
  beginner: "Iniciante",
  intermediate: "Intermediário",
  advanced: "Avançado",
};

export default function TutorialDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [tutorial, setTutorial] = useState<Tutorial | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    apiFetch(`/api/tutorials/${slug}`)
      .then((json) => {
        if (json.success) {
          setTutorial(json.data);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent-600 border-t-transparent" />
      </div>
    );
  }

  if (notFound || !tutorial) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center">
          <h1 className="text-4xl font-bold">404</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Tutorial não encontrado.
          </p>
          <a href="/tutoriais" className="mt-4 inline-block text-accent-600 hover:underline">
            ← Voltar para Tutoriais
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO
        title={tutorial.title}
        description={tutorial.description || `Tutorial: ${tutorial.title}`}
        keywords={[tutorial.category, "tutorial", "zweb", "guia"]}
        ogType="article"
        canonicalUrl={`/tutoriais/${tutorial.slug}`}
        structuredData={getHowToSchema({
          title: tutorial.title,
          description: tutorial.description || "",
          estimatedTime: tutorial.estimatedTime || undefined,
          slug: tutorial.slug,
        })}
      />

      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
        <div className="container flex h-16 items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-600 text-white font-bold">
              Z
            </div>
            <span className="text-xl font-bold">ZWeb</span>
          </a>
          <nav className="flex items-center gap-6">
            <a href="/" className="text-sm font-medium hover:text-accent-600">Home</a>
            <a href="/tutoriais" className="text-sm font-medium text-accent-600">Tutoriais</a>
            <a href="/blog" className="text-sm font-medium hover:text-accent-600">Blog</a>
          </nav>
        </div>
      </header>

      <main className="container py-12">
        <article className="mx-auto max-w-3xl">
          <a href="/tutoriais" className="text-sm text-accent-600 hover:underline">
            ← Voltar para Tutoriais
          </a>

          <div className="mt-6 flex items-center gap-3">
            <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800 dark:bg-accent-900 dark:text-accent-300">
              {categoryLabels[tutorial.category] || tutorial.category}
            </span>
            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
              {difficultyLabels[tutorial.difficulty]}
            </span>
            {tutorial.estimatedTime && (
              <span className="text-sm text-gray-500">
                {tutorial.estimatedTime} min
              </span>
            )}
          </div>

          <h1 className="mt-4 text-4xl font-bold leading-tight">{tutorial.title}</h1>

          {tutorial.description && (
            <p className="mt-4 text-xl text-gray-600 dark:text-gray-400">
              {tutorial.description}
            </p>
          )}

          {tutorial.videoUrl && (
            <div className="mt-8 aspect-video overflow-hidden rounded-2xl">
              <iframe
                src={tutorial.videoUrl}
                title={tutorial.title}
                className="h-full w-full"
                allowFullScreen
              />
            </div>
          )}

          {tutorial.coverImage && !tutorial.videoUrl && (
            <img
              src={tutorial.coverImage}
              alt={tutorial.title}
              className="mt-8 w-full rounded-2xl object-cover"
            />
          )}

          <div
            className="prose prose-lg mt-8 max-w-none dark:prose-invert prose-headings:font-bold prose-a:text-accent-600"
            dangerouslySetInnerHTML={{ __html: tutorial.content }}
          />

          {/* CTA */}
          <div className="mt-12 rounded-2xl bg-gradient-to-br from-accent-500 to-violet-600 p-8 text-center text-white">
            <h3 className="text-2xl font-bold">Precisa de ajuda?</h3>
            <p className="mt-2 text-accent-100">
              Nossa equipe de suporte está pronta para ajudar você.
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <a
                href="/chamados"
                className="rounded-lg bg-white px-6 py-3 font-medium text-accent-600 hover:bg-accent-50"
              >
                Abrir Chamado
              </a>
              <a
                href="https://wa.me/5511999999999"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg border border-white/30 px-6 py-3 font-medium text-white hover:bg-white/10"
              >
                WhatsApp
              </a>
            </div>
          </div>
        </article>
      </main>

      <footer className="border-t border-gray-200 bg-white py-8 dark:border-gray-800 dark:bg-gray-950">
        <div className="container text-center text-sm text-gray-600 dark:text-gray-400">
          © 2026 ZWeb. Todos os direitos reservados.
        </div>
      </footer>
    </div>
  );
}
